import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { authSelectAllowExtendedTools } from "../reduxFolder/selectors/globalSelectors";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeModeContextValue {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const STORAGE_KEY = "theme-mode";
const DEFAULT_MODE: ThemeMode = "light";

// localStorage can throw in private mode, some embedded contexts, or when
// storage is disabled. Fail open to the default rather than crashing the app.
const getStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // ignore — fall through to default
  }
  return DEFAULT_MODE;
};

const persistMode = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore — user's choice won't persist but the session still works
  }
};

const getSystemPreference = (): ResolvedTheme => {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
};

const resolveTheme = (mode: ThemeMode): ResolvedTheme => {
  if (mode === "system") {
    return getSystemPreference();
  }
  return mode;
};

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: DEFAULT_MODE,
  resolvedTheme: "light",
  setMode: () => {},
});

interface ThemeModeProviderProps {
  children: ReactNode;
  allowExtendedTools?: boolean;
}

const ThemeModeProviderBase: FC<ThemeModeProviderProps> = ({
  children,
  allowExtendedTools = false,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode);
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(
    getSystemPreference,
  );

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    persistMode(newMode);
  }, []);

  // Listen for OS-level color scheme changes. iOS Safari <14 doesn't support
  // MediaQueryList.addEventListener, so fall back to the legacy addListener.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    // Legacy Safari fallback
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Users without extended_tools have no UI to change the theme, so force light
  // regardless of stored mode or OS preference. Stored value is left intact so
  // it re-applies if the flag returns.
  const naturalResolved: ResolvedTheme =
    mode === "system" ? systemPreference : mode;
  const resolvedTheme: ResolvedTheme = allowExtendedTools
    ? naturalResolved
    : "light";

  // Keep body class in sync for the blocking script and any CSS that needs it.
  // Also clear the inline body styles set by the FOUC-prevention script in
  // index.html — once React is mounted, styled-components' GlobalStyle is the
  // single source of truth and inline styles would otherwise override it on
  // theme switches.
  useEffect(() => {
    document.body.classList.remove("theme-light", "theme-dark");
    document.body.classList.add(`theme-${resolvedTheme}`);
    document.body.style.backgroundColor = "";
    document.body.style.color = "";
  }, [resolvedTheme]);

  const value = useMemo(
    () => ({ mode, resolvedTheme, setMode }),
    [mode, resolvedTheme, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

const withConnect = connect(
  createStructuredSelector({
    allowExtendedTools: authSelectAllowExtendedTools,
  }),
);

export const ThemeModeProvider = withConnect(ThemeModeProviderBase);

export const useThemeMode = (): ThemeModeContextValue =>
  useContext(ThemeModeContext);
